import React from 'react';

import { EvEllipsedTextWithTooltip } from '@evinced-private/ui-common';

import { IEvBreadcrumb } from 'src/common/components/ev-breadcrumbs/EvBreadcrumbs';
import { LabelsPopup } from 'src/common/components/labels-popup/LabelsPopup';
import { IReportInfo } from 'src/common/components/report-info/ReportInfo';
import { UNIT_ID_LABEL_KEY } from 'src/common/consts/platformConfigConsts';
import { formatLabel } from 'src/common/helpers/DataItemHelper';
import { formatDate, formatShortMonthDatetime } from 'src/common/helpers/DateFormatHelper';
import { getPropertiesString } from 'src/common/helpers/PropertiesHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { DevelopmentToggles, getSingleToggle } from 'src/common/services/TogglesService';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';
import { PlatformLabel } from 'src/common/types/IssueDetailsType';

import { capitalizeFirstLetter } from '../../../common/helpers/FormatHelper';
import { IWebSdkTest, IWebSdkTestDetails } from '../types/WebSdkDashboardServiceTypes';

import {
	extractEnvironmentValue,
	extractUnitIdValue,
	formatEnvironmentDisplayValue,
	formatUnitIdDisplayValue,
	isEnvironmentLabelString,
	isUnitIdLabelString
} from './LabelDisplayHelpers';
import { MAIN_PAGE_NAV_LABEL, NAME_NOT_PROVIDED } from './WebSdkConsts';

const enableWebSdkProperties = getSingleToggle(DevelopmentToggles.ENABLE_WEB_SDK_PROPERTIES);
const ENVIRONMENT_LABEL_KEY = 'environment';

export const getWebSdkDashboardBreadcrumbsRoutes = (
	runId: string,
	testName: string = NAME_NOT_PROVIDED,
	runTime?: string
): IEvBreadcrumb[] => {
	return [
		{
			id: MAIN_PAGE_NAV_LABEL,
			url: RoutesHelper.getWebSdkDashboardPath(),
			title: MAIN_PAGE_NAV_LABEL
		},
		{
			id: runId,
			url: '',
			title: `${testName}${runTime ? ` - ${formatDate(runTime)}` : ''}`
		}
	];
};

export const populatePropertiesLabel = (
	labels: PlatformLabel[],
	propertyIds: string[],
	propertiesMap?: Map<string, string>
): PlatformLabel[] => {
	const propertiesLabelIndex = labels.findIndex((label) => label.name === 'Properties');
	if (propertiesLabelIndex > -1) {
		const labelsClone = [...labels];
		if (enableWebSdkProperties) {
			const propertiesLabel = labels[propertiesLabelIndex];

			// if properties are enabled, replace property label placeholder with the actial value
			labelsClone.splice(propertiesLabelIndex, 1, {
				...propertiesLabel,
				value: capitalizeFirstLetter(getPropertiesString(propertyIds, propertiesMap) ?? '') || 'N/A'
			});
		} else {
			// if properties are not enabled, remove properties label placeholder
			labelsClone.splice(propertiesLabelIndex, 1);
		}
		return labelsClone;
	}
	return labels;
};

const transformPlatformLabelsForDisplay = (
	labels: PlatformLabel[],
	isNewPlatformEnabled: boolean,
	unitIdDisplayName?: string,
	unitIdLabelConfig?: ConfigurationLabel,
	environmentDisplayName?: string,
	environmentLabelConfig?: ConfigurationLabel
): PlatformLabel[] => {
	if (!isNewPlatformEnabled || (!unitIdDisplayName && !environmentDisplayName)) {
		return labels;
	}

	return labels.map((label) => {
		const isUnitIdByName = label.name.toLowerCase() === UNIT_ID_LABEL_KEY.toLowerCase();
		const isUnitIdByValue = isUnitIdLabelString(label.value);
		const isUnitIdLabel = isUnitIdByName || isUnitIdByValue;

		if (isUnitIdLabel && unitIdDisplayName) {
			const value = isUnitIdByValue ? extractUnitIdValue(label.value) : label.value.trim();
			const displayValue = formatUnitIdDisplayValue(value, unitIdDisplayName, unitIdLabelConfig);

			return {
				...label,
				name: unitIdDisplayName,
				value: displayValue
			};
		}

		const isEnvironmentByName = label.name.toLowerCase() === ENVIRONMENT_LABEL_KEY.toLowerCase();
		const isEnvironmentByValue = isEnvironmentLabelString(label.value);
		const isEnvironmentLabel = isEnvironmentByName || isEnvironmentByValue;

		if (isEnvironmentLabel && environmentDisplayName) {
			const value = isEnvironmentByValue
				? extractEnvironmentValue(label.value)
				: label.value.trim();
			const displayValue = formatEnvironmentDisplayValue(
				value,
				environmentDisplayName,
				environmentLabelConfig
			);

			return {
				...label,
				name: environmentDisplayName,
				value: displayValue
			};
		}

		return label;
	});
};

export const getWebSdkInfoItems = (
	propertiesMap?: Map<string, string>,
	isNewPlatformEnabled = false,
	unitIdDisplayName?: string,
	unitIdLabelConfig?: ConfigurationLabel,
	environmentDisplayName?: string,
	environmentLabelConfig?: ConfigurationLabel
): IReportInfo[] => [
	{ key: 'runTime', label: 'Test date', textFormatter: formatShortMonthDatetime },
	{
		key: 'testPath',
		label: 'Test path',
		isMiddleEllipsis: true,
		textFormatter: capitalizeFirstLetter
	},
	...(enableWebSdkProperties
		? [
				{
					key: 'propertyIds',
					label: 'Properties',
					customValueRenderer: (test: IWebSdkTestDetails): JSX.Element | null => {
						return test.propertyIds?.length ? (
							<EvEllipsedTextWithTooltip
								text={capitalizeFirstLetter(
									getPropertiesString(test.propertyIds, propertiesMap) ?? ''
								)}
								maxWidth={200}
								showTooltipOnlyOnEllipsed
							/>
						) : null;
					},
					labelFormatter: formatLabel
				} as IReportInfo
			]
		: []),
	{ key: 'environments', label: 'Environment', textFormatter: capitalizeFirstLetter },
	{ key: 'flows', label: 'Flow', textFormatter: capitalizeFirstLetter },
	{
		key: 'labels',
		label: 'Labels',
		customValueRenderer: (test: IWebSdkTestDetails) => {
			const labelsWithProperties = populatePropertiesLabel(
				test.labels,
				test.propertyIds,
				propertiesMap
			);

			const displayLabels = transformPlatformLabelsForDisplay(
				labelsWithProperties,
				isNewPlatformEnabled,
				unitIdDisplayName,
				unitIdLabelConfig,
				environmentDisplayName,
				environmentLabelConfig
			);
			return <LabelsPopup name={test.name} labels={displayLabels} title="View all test labels" />;
		},
		labelFormatter: formatLabel
	}
];

export const populateTestsTableDataWithDisplayData = (
	data: IWebSdkTest[],
	showTestPath: boolean,
	propertiesMap?: Map<string, string>
): IWebSdkTest[] =>
	data.map((row) => ({
		...row,
		testDisplayName: showTestPath ? `${row.testPath}/${row.testName}` : row.testName,
		propertiesString: getPropertiesString(row.propertyIds, propertiesMap) ?? 'N/A'
	}));
